import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../api.js";
import { extractFooterSupport } from "../legacy/utils/publicFooter.js";
import { CORE_STATIC_SLUGS, STATIC_PAGE_CONTENT } from "../content/staticPageContent.js";
import useIsMobile from "../hooks/useIsMobile.js";
import MobileBottomNav from "../mobile/components/MobileBottomNav.jsx";
import MobileHeader from "../mobile/components/MobileHeader.jsx";
import "./staticPage.css";

const FOOTER_PAGES = [
  {
    slug: "about",
    label: "About Us",
    description: "Our story and the Homevilas promise.",
  },
  {
    slug: "contact",
    label: "Contact Us",
    description: "Get in touch with our guest experience team.",
  },
  {
    slug: "privacy",
    label: "Privacy Policy",
    description: "How we collect, use, and safeguard data.",
  },
  {
    slug: "terms",
    label: "Terms & Conditions",
    description: "Booking policies, usage rules, and guidelines.",
  },
];

const CONTACT_EMAIL = "info@homevilas.com, meena@homevilas.com";
const CONTACT_PHONE = "+918287703118";
const CONTACT_ADDRESS = "E-6, Kalkaji, New Delhi-110019";

export default function StaticPage({ slug }) {
  const fallbackMeta = {
    about: {
      title: "About Us",
      body:
        "Homevilas curates premium villas, homestays, cabs, and holiday journeys across India. We focus on trusted stays, elevated service, and tailored travel support for guests, families, and groups.",
    },
    contact: {
      title: "Contact Us",
      body:
        "Need help with bookings, pricing, or planning your next stay? Reach out to the Homevilas guest experience team and we will assist you with stays, packages, and transport options.\n\nEmail: info@homevilas.com, meena@homevilas.com\nPhone: +918287703118\nAddress: E-6, Kalkaji, New Delhi-110019",
    },
    privacy: {
      title: "Privacy Policy",
      body:
        "Homevilas respects your privacy and handles your personal information carefully. This page covers how data is collected, used, stored, and protected across bookings, account access, and customer support.",
    },
    terms: {
      title: "Terms & Conditions",
      body:
        "These terms describe the rules for using Homevilas services, including browsing, account use, booking requests, pricing, cancellations, and support processes.",
    },
    careers: {
      title: "Careers",
      body:
        "Careers at Homevilas will be published here soon. If you want to work with us in hospitality, partnerships, operations, or product, please contact the team through the contact page.",
    },
    help: {
      title: "Help Center",
      body:
        "Need assistance with a booking, payment, account, or property enquiry? Use this help center page to find support details and get guidance from the Homevilas team.",
    },
    "partner-with-us": {
      title: "Partner With Us",
      body:
        "Partner with Homevilas for B2B collaborations, curated travel experiences, distribution opportunities, and hospitality growth initiatives. Use this page to start a business conversation with our team.",
    },
    "b2b-registration": {
      title: "B2B Registration",
      body:
        "Register your business interest with Homevilas for B2B partnerships, travel collaborations, and corporate enquiries. Our partnerships team will get in touch after reviewing your request.",
    },
  };
  const resolvedSlug = useMemo(() => (slug ? String(slug).trim() : ""), [slug]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(Boolean(resolvedSlug));
  const [error, setError] = useState("");
  const [support, setSupport] = useState({
    email: CONTACT_EMAIL,
    phone: CONTACT_PHONE,
    address: CONTACT_ADDRESS,
  });
  const location = useLocation();
  const activeSlug = resolvedSlug || location.pathname.replace("/", "");
  const isMobile = useIsMobile();
  const localPage = STATIC_PAGE_CONTENT[activeSlug] || null;
  const isCoreStaticPage = CORE_STATIC_SLUGS.includes(activeSlug);
  const fallback = fallbackMeta[activeSlug] || null;
  const pageMeta = FOOTER_PAGES.find((item) => item.slug === activeSlug);
  const introText = useMemo(() => {
    if (localPage?.excerpt) return localPage.excerpt;
    const firstLine = String(body || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find(Boolean);
    if (!firstLine) return "";
    return firstLine.length > 180 ? `${firstLine.slice(0, 177)}...` : firstLine;
  }, [body, localPage?.excerpt]);

  useEffect(() => {
    let active = true;
    if (!resolvedSlug) {
      setLoading(false);
      return () => {};
    }
    const load = async () => {
      setLoading(true);
      setError("");
      if (isCoreStaticPage && localPage) {
        setTitle(localPage.title || pageMeta?.label || "");
        setBody("");
        setHtml(localPage.html || "");
        try {
          const footerRes = await api.get("/public/footer").catch(() => null);
          if (!active) return;
          const footerSupport = extractFooterSupport(footerRes?.data);
          setSupport({
            email: footerSupport.email || CONTACT_EMAIL,
            phone: footerSupport.phone || CONTACT_PHONE,
            address: footerSupport.address || CONTACT_ADDRESS,
          });
        } catch {
          if (!active) return;
          setSupport({
            email: CONTACT_EMAIL,
            phone: CONTACT_PHONE,
            address: CONTACT_ADDRESS,
          });
        } finally {
          if (!active) return;
          setLoading(false);
        }
        return;
      }
      try {
        const [res, footerRes] = await Promise.all([
          api.get(`/public/pages/${resolvedSlug}`),
          api.get("/public/footer").catch(() => null),
        ]);
        const payload = res?.data?.data || res?.data || {};
        if (!active) return;
        setTitle(payload.title || fallback?.title || pageMeta?.label || "");
        setBody(payload.body || fallback?.body || "");
        setHtml("");
        const footerSupport = extractFooterSupport(footerRes?.data);
        setSupport({
          email: footerSupport.email || CONTACT_EMAIL,
          phone: footerSupport.phone || CONTACT_PHONE,
          address: footerSupport.address || CONTACT_ADDRESS,
        });
      } catch (err) {
        if (!active) return;
        setError(fallback?.title || fallback?.body ? "" : err?.response?.data?.message || "");
        setTitle(fallback?.title || pageMeta?.label || "");
        setBody(fallback?.body || "");
        setHtml("");
        setSupport({
          email: CONTACT_EMAIL,
          phone: CONTACT_PHONE,
          address: CONTACT_ADDRESS,
        });
      } finally {
        if (!active) return;
        setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [resolvedSlug, pageMeta?.label, fallback?.body, fallback?.title, isCoreStaticPage, localPage]);

  useEffect(() => {
    if (title) {
      document.title = `${title} | Homevilas`;
    }
  }, [title]);

  return (
    <div className="min-h-screen flex flex-col bg-[var(--page-bg)]">
      {isMobile ? <MobileHeader showBack={activeSlug !== "about" && activeSlug !== ""} /> : null}
      <section className="relative overflow-hidden">
        <div className="absolute -top-28 right-[-120px] h-64 w-64 rounded-full bg-[radial-gradient(circle_at_center,rgba(201,169,110,0.35),rgba(201,169,110,0))] blur-2xl" />
        <div className="absolute left-[-80px] top-24 h-56 w-56 rounded-full bg-[radial-gradient(circle_at_center,rgba(15,43,51,0.18),rgba(15,43,51,0))] blur-2xl" />
        <div className="landing-container relative py-16">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-white/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-[var(--muted)] shadow-soft-sm">
              Homevilas
            </div>
            <h1 className="text-4xl md:text-5xl font-homevilas text-[#1c1c1c]">
              {title || "Homevilas"}
            </h1>
            {introText ? (
              <p className="max-w-2xl text-sm md:text-base leading-relaxed text-[var(--text-muted)]">
                {introText}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      <section className="landing-container flex-1 pb-16">
        <div className="grid gap-8 lg:grid-cols-[260px,1fr]">
          <aside className="space-y-6">
            <div className="rounded-2xl border border-[var(--border)] bg-white/90 p-5 shadow-soft-sm">
              <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[var(--muted)]">
                Explore
              </div>
              <div className="mt-4 space-y-2">
                {FOOTER_PAGES.map((item) => {
                  const isActive = item.slug === activeSlug;
                  return (
                    <Link
                      key={item.slug}
                      to={`/${item.slug}`}
                      className={`flex flex-col gap-1 rounded-xl border px-4 py-3 text-left transition ${
                        isActive
                          ? "border-[#c9a96e] bg-[#fff7ec] text-[#3d3328] shadow-[0_10px_24px_rgba(201,169,110,0.18)]"
                          : "border-transparent bg-[#f7f0e8] text-[#5e5448] hover:border-[#e8dac8] hover:bg-white"
                      }`}
                    >
                      <span className="text-sm font-semibold">{item.label}</span>
                      <span className="text-xs text-[var(--text-muted)]">
                        {item.description}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="rounded-2xl border border-[var(--border)] bg-white/90 p-5 shadow-soft-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--muted)]">
                Need Help?
              </div>
              <p className="mt-3 text-sm text-[var(--text-muted)]">
                Our team can help with bookings, policies, and tailored stays.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {support.email ? (
                  <span className="rounded-full bg-[#f3eee6] px-3 py-1 text-xs font-semibold text-[#6f6253]">
                    {support.email}
                  </span>
                ) : null}
                {support.phone ? (
                  <span className="rounded-full bg-[#f3eee6] px-3 py-1 text-xs font-semibold text-[#6f6253]">
                    {support.phone}
                  </span>
                ) : null}
                {support.address ? (
                  <span className="rounded-full bg-[#f3eee6] px-3 py-1 text-xs font-semibold text-[#6f6253]">
                    {support.address}
                  </span>
                ) : null}
              </div>
            </div>
          </aside>

          <main className="rounded-3xl border border-[var(--border)] bg-white/95 p-6 md:p-10 shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
            {loading ? (
              <div className="space-y-3">
                <div className="h-4 w-2/3 rounded-full bg-slate-200" />
                <div className="h-4 w-full rounded-full bg-slate-200" />
                <div className="h-4 w-11/12 rounded-full bg-slate-200" />
              </div>
            ) : (
              html ? (
                <div
                  className="hv-static-content space-y-4"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ) : (
                <div className="space-y-4 text-[15px] leading-relaxed text-slate-700 whitespace-pre-line">
                  {body || "Content is not available."}
                </div>
              )
            )}
            {!loading && error ? (
              <p className="mt-4 text-sm text-slate-400">{error}</p>
            ) : null}
          </main>
        </div>
      </section>
      {isMobile ? <MobileBottomNav /> : null}
    </div>
  );
}
